// Copyright (c) StrangeBee 2024

import client from '@Client';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import useValidateForm from '@Hooks/useValidateForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import RouteBuilder from '@Services/RouteBuilder';
import React, { ReactElement } from 'react';
import { useHistory } from 'react-router';
import { CaseOutput, FilterInput } from 'thehive-sdk';
import CaseMerge, { CaseMergeFormValues } from './CaseMerge';

interface CaseMergeProps {
    caseId: string;
    triggerElem: ReactElement;
    listCasesForMerge: (filter: FilterInput) => Promise<CaseOutput[]>;
}

const CaseMergeDrawer = ({ caseId, triggerElem, listCasesForMerge }: CaseMergeProps): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinish = ({ caseData }: CaseMergeFormValues): Promise<any> => {
        if (caseData && caseId !== caseData._id) {
            return client.CaseController.mergeCases(caseId, caseData._id)
                .then(({ _id }) => {
                    openMessage('success', i('case.merge.success'));
                    history.push(RouteBuilder.compute('cases.details', { caseId: _id }));
                })
                .catch(handleNotificationError);
        }

        return Promise.reject();
    };

    return (
        <THDrawer
            title={i('case.merge.title')}
            triggerElem={triggerElem}
            className="drawer-form"
            afterVisibleChange={() => form.resetFields()}
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <CaseMerge
                    caseId={caseId}
                    form={form}
                    disabled={!isValid || !fieldsTouched}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    onFinish={(values) =>
                        onFinish(values).finally(() => {
                            toggleDrawer();
                            setFieldsTouched(false);
                        })
                    }
                    onCancel={() => {
                        setFieldsTouched(false);
                        toggleDrawer();
                        form.resetFields();
                    }}
                    listCasesForMerge={listCasesForMerge}
                />
            )}
        </THDrawer>
    );
};

export default CaseMergeDrawer;
